import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { InboxResponse } from "@mubin/contracts";
import { api, errorText } from "../lib/api";
import { useMe } from "../lib/session";
import { Badge, Empty, Loading } from "./ui";

const sessionStatus: Record<string, string> = { failed: "فشلت", expired: "انتهت مهلتها", missed: "فاتت" };
const day = (iso: string) => iso.slice(0, 10);

/** «ما يحتاج إجراء» on the home page (docs/09 §2), from GET /v1/admin/inbox. */
export function NeedsAction() {
  const { data: me } = useMe();
  const inbox = useQuery({
    queryKey: ["inbox", me?.activeRole],
    queryFn: () => api<InboxResponse>("/v1/admin/inbox"),
  });
  const d = inbox.data;
  const total = d
    ? d.draftPlans.length +
      d.examsToApprove.length +
      d.overdueExams.length +
      d.circlesWithoutTeacher.length +
      d.absenceCorrections.length +
      d.failedSessions.length
    : 0;

  return (
    <section className="card" aria-labelledby="todo-h">
      <div className="card-h">
        <h2 id="todo-h">يحتاج إجراء</h2>
        {d && <span className="num">{total}</span>}
      </div>
      {inbox.isPending ? (
        <Loading />
      ) : inbox.isError ? (
        <Empty title="تعذر التحميل">{errorText(inbox.error)}</Empty>
      ) : !d || total === 0 ? (
        <Empty title="لا شيء ينتظرك الآن">
          تظهر هنا الخطط والاختبارات التي تنتظر الاعتماد وما يحتاج متابعة.
        </Empty>
      ) : (
        <div className="todo">
          <Group title="خطط بانتظار الاعتماد" count={d.draftPlans.length}>
            {d.draftPlans.map((p) => (
              <li key={p.planId}>
                <Link className="link" to="/plan-preview" search={{ enrollmentId: p.enrollmentId }}>
                  {p.student.name}
                </Link>
                <span className="muted small">
                  {" "}
                  · {p.circle.name} · {day(p.createdAt)}
                  {p.proposedBy && ` · مقترحة من المعلم: ${p.proposedBy.name}`}
                </span>
              </li>
            ))}
          </Group>
          <Group title="اختبارات بانتظار اعتمادك" count={d.examsToApprove.length}>
            {d.examsToApprove.map((x) => (
              <li key={x.examId}>
                <Link className="link" to="/users/$userId" params={{ userId: x.student.id }}>
                  {x.student.name}
                </Link>
                <span className="muted small"> · {x.typeName} · </span>
                <span className="num small">
                  {x.score} / {x.maxScore}
                </span>{" "}
                <Badge tone={x.passed ? "success" : "warning"}>{x.passed ? "ناجح" : "لم يجتز"}</Badge>
              </li>
            ))}
          </Group>
          <Group title="اختبارات فات موعدها دون درجة" count={d.overdueExams.length}>
            {d.overdueExams.map((x) => (
              <li key={x.examId}>
                <Link className="link" to="/users/$userId" params={{ userId: x.student.id }}>
                  {x.student.name}
                </Link>
                <span className="muted small">
                  {" "}
                  · {x.typeName} · {x.scheduledOn} · المختبِر: {x.examiner.name}
                </span>
              </li>
            ))}
          </Group>
          <Group title="حلقات بلا معلم" count={d.circlesWithoutTeacher.length}>
            {d.circlesWithoutTeacher.map((c) => (
              <li key={c.id}>
                <Link className="link" to="/circles/$circleId" params={{ circleId: c.id }}>
                  {c.name}
                </Link>
                <span className="muted small"> · {c.branchName}</span>
              </li>
            ))}
          </Group>
          <Group title="غياب صُحّح ولم تُرجَع خطته" count={d.absenceCorrections.length}>
            {d.absenceCorrections.map((x) => (
              <li key={`${x.planId}:${x.date}`}>
                <Link className="link" to="/plan-preview" search={{ enrollmentId: x.enrollmentId }}>
                  {x.student.name}
                </Link>
                <span className="muted small">
                  {" "}
                  · {x.circle.name} · يوم <bdi dir="ltr">{x.date}</bdi> · سمّع بعده فبقيت الخطة كما هي، راجعها
                </span>
              </li>
            ))}
          </Group>
          <Group title="جلسات تسميع لم تتم (آخر 7 أيام)" count={d.failedSessions.length}>
            {d.failedSessions.map((s) => (
              <li key={s.id}>
                <Link className="link" to="/users/$userId" params={{ userId: s.student.id }}>
                  {s.student.name}
                </Link>
                <span className="muted small">
                  {" "}
                  · {s.circle.name} · {sessionStatus[s.status] ?? s.status} · {day(s.at)}
                </span>
              </li>
            ))}
          </Group>
        </div>
      )}
    </section>
  );
}

function Group({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <div className="todo-group">
      <h3>
        {title} <span className="num muted">({count})</span>
      </h3>
      <ul>{children}</ul>
    </div>
  );
}
